import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { track as ciTrack } from "@/lib/customer-intelligence/tracker";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PlanView } from "@/components/diet/PlanView";
import { useT } from "@/lib/i18n/provider";
import { InlineConsentCard } from "@/components/consent/InlineConsentCard";
import {
  ConsentBlock,
  defaultConsentSelection,
  type ConsentSelection,
} from "@/components/consent/ConsentBlock";
import { claimDietPlan, createDietPlan } from "@/lib/diet/diet-plan.functions";
import { supabase } from "@/integrations/supabase/client";
import { getAnonymousId } from "@/lib/identity/anonymousId";
import {
  ACTIVITY_LEVELS,
  CONDITIONS,
  DIET_TYPES,
  REGIONS,
  TRACKS,
  bmi,
  type ActivityLevel,
  type ConditionKey,
  type DietTrack,
  type DietType,
  type GeneratedPlan,
  type Region,
} from "@/lib/diet/plan";

const DIET_PLAN_URL = "https://truwe.in/diet-plan";

const DIET_FAQ = [
  {
    q: "Is the diet plan really free?",
    a: "Yes. The 7-day Indian meal plan, daily routine and grocery list are free — there is no payment step and no card needed.",
  },
  {
    q: "How personalised is the plan?",
    a: "It is built from your goal (PCOS, fatty liver or general wellness), age, height, weight, activity level, food preference, region of India and any conditions you tell us about. Calories and protein are calculated for your body, not copied from a template.",
  },
  {
    q: "Can I get a PCOS or fatty liver diet plan?",
    a: "Yes. Pick the PCOS or fatty liver track and the meals, routine and food swaps are adjusted for that goal.",
  },
  {
    q: "Can I download the plan as a PDF?",
    a: "Yes. Once your plan is generated you can save or print it as a PDF, and it stays in your TruWe account.",
  },
  {
    q: "Does it replace a doctor or dietitian?",
    a: "No. It is general food and routine guidance, not medical advice or treatment. If you are pregnant, managing a diagnosed condition or on medication, check with your doctor first.",
  },
];

export const Route = createFileRoute("/diet-plan")({
  head: () => ({
    meta: [
      { title: "Free Diet Plan Online — 7-Day Indian Meal Plan | TruWe" },
      {
        name: "description",
        content:
          "Get a free personalised 7-day Indian diet plan and daily routine — for PCOS, fatty liver or everyday energy. Includes grocery list and PDF download. No payment.",
      },
      { property: "og:title", content: "Free 7-Day Indian Diet Plan & Routine | TruWe" },
      {
        property: "og:description",
        content:
          "Answer a few questions and get a personalised 7-day Indian meal plan, daily routine and grocery list. Free, no payment needed.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: DIET_PLAN_URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: DIET_PLAN_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebApplication",
              name: "TruWe Free Diet Plan & Routine Generator",
              url: DIET_PLAN_URL,
              applicationCategory: "HealthApplication",
              operatingSystem: "Any (web browser)",
              description:
                "Free personalised 7-day Indian diet plan, daily routine and grocery list for PCOS, fatty liver or general wellness.",
              offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
            },
            {
              "@type": "FAQPage",
              mainEntity: DIET_FAQ.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "TruWe", item: "https://truwe.in/" },
                { "@type": "ListItem", position: 2, name: "Free diet plan", item: DIET_PLAN_URL },
              ],
            },
          ],
        }),
      },
    ],
  }),
  component: DietPlanPage,
});


const DIET_TYPE_KEY: Record<DietType, string> = {
  vegetarian: "diet.dietType.vegetarian",
  eggetarian: "diet.dietType.eggetarian",
  "non-vegetarian": "diet.dietType.non-vegetarian",
  vegan: "diet.dietType.vegan",
  jain: "diet.dietType.jain",
};
const REGION_KEY: Record<Region, string> = {
  north: "diet.region.north",
  south: "diet.region.south",
  east: "diet.region.east",
  west: "diet.region.west",
  northeast: "diet.region.northeast",
};
const ACTIVITY_KEY: Record<ActivityLevel, string> = {
  sedentary: "diet.activity.sedentary",
  light: "diet.activity.light",
  moderate: "diet.activity.moderate",
  active: "diet.activity.active",
};
const TRACK_LABEL_KEY: Record<DietTrack, string> = {
  pcos: "diet.track.pcos.label",
  "fatty-liver": "diet.track.fatty-liver.label",
  wellness: "diet.track.wellness.label",
};
const TRACK_BLURB_KEY: Record<DietTrack, string> = {
  pcos: "diet.track.pcos.blurb",
  "fatty-liver": "diet.track.fatty-liver.blurb",
  wellness: "diet.track.wellness.blurb",
};
const CONDITION_LABEL_KEY: Record<ConditionKey, string> = {
  none: "diet.condition.none",
  thyroid: "diet.condition.thyroid",
  diabetes_t2: "diet.condition.diabetes_t2",
  diabetes_t1: "diet.condition.diabetes_t1",
  hypertension: "diet.condition.hypertension",
  kidney: "diet.condition.kidney",
  liver: "diet.condition.liver",
  heart: "diet.condition.heart",
  gi: "diet.condition.gi",
  pregnant: "diet.condition.pregnant",
  eating_disorder: "diet.condition.eating_disorder",
  cancer: "diet.condition.cancer",
};
const TRACK_QUESTION_KEY: Record<DietTrack, string> = {
  pcos: "diet.trackQuestion.pcos",
  "fatty-liver": "diet.trackQuestion.fatty-liver",
  wellness: "diet.trackQuestion.wellness",
};
function bmiBandKey(value: number): string {
  if (value < 18.5) return "diet.bmiBand.under";
  if (value < 23) return "diet.bmiBand.healthy";
  if (value < 25) return "diet.bmiBand.above";
  if (value < 30) return "diet.bmiBand.overweight";
  return "diet.bmiBand.obese";
}
const GATE_MESSAGE_KEY: Record<string, string> = {
  "This plan is built for adults. For anyone under 18, please speak with a paediatrician or a registered dietitian.":
    "diet.gate.under_18",
  "Above 85, nutrition targets need individual medical review. Please speak with your doctor.":
    "diet.gate.over_85",
  "Your BMI is in a range where a self-serve plan isn't appropriate. Please see a doctor or clinical dietitian first.":
    "diet.gate.bmi_low",
  "Nutrition during pregnancy and breastfeeding needs to be supervised by your obstetrician or a clinical dietitian — a generated plan isn't safe here.":
    "diet.gate.condition_pregnant",
  "With a history of disordered eating, structured calorie plans can do more harm than good. Please work with a clinician you trust.":
    "diet.gate.condition_eating_disorder",
  "Type 1 diabetes needs carbohydrate planning matched to your insulin, which only your treating team can do safely.":
    "diet.gate.condition_diabetes_t1",
  "Kidney disease changes protein, potassium and fluid targets significantly. Please get a plan from your nephrology team.":
    "diet.gate.condition_kidney",
  "During or shortly after cancer treatment, nutrition should be planned with your oncology team.":
    "diet.gate.condition_cancer",
};

type FormState = {
  track: DietTrack;
  name: string;
  email: string;
  phone: string;
  gender: "female" | "male" | "other";
  age: string;
  heightCm: string;
  weightKg: string;
  dietType: DietType;
  region: Region;
  activity: ActivityLevel;
  sleepHours: string;
  conditions: ConditionKey[];
  medications: string;
  allergies: string;
  trackNote: string;
  consentChannels: ConsentSelection;
};

const INITIAL: FormState = {
  track: "pcos",
  name: "",
  email: "",
  phone: "",
  gender: "female",
  age: "",
  heightCm: "",
  weightKg: "",
  dietType: "vegetarian",
  region: "north",
  activity: "light",
  sleepHours: "7",
  conditions: [],
  medications: "",
  allergies: "",
  trackNote: "",
  consentChannels: defaultConsentSelection(["email", "whatsapp"]),
};

function DietPlanPage() {
  const t = useT();
  const generate = useServerFn(createDietPlan);
  const claim = useServerFn(claimDietPlan);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(INITIAL);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ plan: GeneratedPlan; token: string } | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const height = Number(form.heightCm);
  const weight = Number(form.weightKg);
  const bmiValue = height && weight ? bmi(height, weight) : 0;

  // Funnel: one "diet_plan_start" per visit to the intake form.
  useEffect(() => {
    ciTrack("diet_plan_start", {});
  }, []);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await generate({
        data: {
          track: form.track,
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          gender: form.gender,
          age: Number(form.age),
          heightCm: Number(form.heightCm),
          weightKg: Number(form.weightKg),
          dietType: form.dietType,
          region: form.region,
          activity: form.activity,
          sleepHours: Number(form.sleepHours),
          conditions: form.conditions.filter((c) => c !== "none"),
          medications: form.medications.trim(),
          allergies: form.allergies.trim(),
          trackNote: form.trackNote.trim(),
          consentMarketing: Boolean(form.consentChannels.email || form.consentChannels.whatsapp),
          consentChannels: {
            email: form.consentChannels.email === true,
            whatsapp: form.consentChannels.whatsapp === true,
          },
          anonymousId: getAnonymousId() || undefined,
        },
      });
      if (res.ok) {
        setResult({ plan: res.plan, token: res.token });
        ciTrack("diet_plan_generated", { track: form.track, conditions: form.conditions.length });

        // If the visitor is signed in, attach the plan to their account so it
        // shows up under Account → Your diet plans. Ownership is derived from
        // the bearer token server-side; failures are non-blocking.
        try {
          const { data: sess } = await supabase.auth.getSession();
          if (sess.session) await claim({ data: { token: res.token } });
        } catch {
          /* plan is still viewable on screen */
        }
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        setError(t(GATE_MESSAGE_KEY[res.message] as never) || res.message);
      }
    } catch {
      setError(t("diet.error.generic"));
    } finally {
      setBusy(false);
    }
  }

  if (result) {
    return (
      <main className="mx-auto max-w-5xl px-4 py-10 sm:py-14">
        <PlanView plan={result.plan} track={form.track} name={form.name.split(" ")[0]} />
        <div className="mt-10 print:hidden">
          <InlineConsentCard
            source="diet_plan"
            headline={t("diet.result.consentHeadline")}
          />
        </div>
        <div className="mt-6 print:hidden">
          <Button variant="outline" className="rounded-full" onClick={() => setResult(null)}>
            {t("diet.result.another")}
          </Button>
        </div>
      </main>
    );
  }

  const step1Valid =
    form.name.trim().length > 1 &&
    /\S+@\S+\.\S+/.test(form.email) &&
    form.phone.replace(/\D/g, "").length >= 10;
  const step2Valid =
    Number(form.age) >= 5 && Number(form.heightCm) >= 80 && Number(form.weightKg) >= 25;

  return (
    <main className="mx-auto max-w-3xl px-4 py-10 sm:py-14">
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand">
        {t("diet.badge.free")}
      </p>
      <h1 className="mt-2 font-display text-3xl font-semibold sm:text-4xl">
        {t("diet.title")}
      </h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">
        {t("diet.subtitle")}
      </p>

      <div className="mt-8 flex gap-2" aria-hidden>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={`h-1 flex-1 rounded-full ${i <= step ? "bg-brand" : "bg-muted"}`}
          />
        ))}
      </div>

      <div className="mt-8 space-y-6 rounded-3xl border border-border bg-card p-6 sm:p-8">
        {step === 0 ? (
          <>
            <Section title={t("diet.section.track")}>
              <div className="grid gap-3">
                {Object.values(TRACKS).map((track) => (
                  <button
                    key={track.key}
                    type="button"
                    onClick={() => set("track", track.key)}
                    className={`rounded-2xl border p-4 text-left transition ${
                      form.track === track.key
                        ? "border-brand bg-brand-soft/40"
                        : "border-border hover:border-brand/40"
                    }`}
                  >
                    <span className="font-display text-base font-semibold">
                      {t(TRACK_LABEL_KEY[track.key] as never)}
                    </span>
                    <span className="mt-1 block text-sm text-muted-foreground">
                      {t(TRACK_BLURB_KEY[track.key] as never)}
                    </span>
                  </button>
                ))}
              </div>
            </Section>

            <Section title={t("diet.section.contact")}>
              <Field label={t("diet.field.fullName")}>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} />
              </Field>
              <Field label={t("diet.field.email")}>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                />
              </Field>
              <Field label={t("diet.field.whatsapp")}>
                <Input
                  type="tel"
                  inputMode="tel"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                />
              </Field>
            </Section>

            <Button
              className="w-full rounded-full"
              disabled={!step1Valid}
              onClick={() => setStep(1)}
            >
              {t("diet.continue")}
            </Button>
          </>
        ) : null}

        {step === 1 ? (
          <>
            <Section title={t("diet.section.about")}>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label={t("diet.field.age")}>
                  <Input
                    inputMode="numeric"
                    value={form.age}
                    onChange={(e) => set("age", e.target.value)}
                  />
                </Field>
                <Field label={t("diet.field.height")}>
                  <Input
                    inputMode="numeric"
                    value={form.heightCm}
                    onChange={(e) => set("heightCm", e.target.value)}
                  />
                </Field>
                <Field label={t("diet.field.weight")}>
                  <Input
                    inputMode="numeric"
                    value={form.weightKg}
                    onChange={(e) => set("weightKg", e.target.value)}
                  />
                </Field>
              </div>
              {bmiValue > 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t("diet.bmi.prefix")} <strong className="text-foreground">{bmiValue}</strong> ·{" "}
                  {t(bmiBandKey(bmiValue) as never)}
                </p>
              ) : null}
              <Choices
                label={t("diet.choices.gender")}
                options={[
                  ["female", t("diet.gender.female")],
                  ["male", t("diet.gender.male")],
                  ["other", t("diet.gender.other")],
                ]}
                value={form.gender}
                onChange={(v) => set("gender", v as FormState["gender"])}
              />
              <Choices
                label={t("diet.choices.dietType")}
                options={DIET_TYPES.map((d) => [d, t(DIET_TYPE_KEY[d] as never)])}
                value={form.dietType}
                onChange={(v) => set("dietType", v as DietType)}
              />
              <Choices
                label={t("diet.choices.region")}
                options={REGIONS.map((r) => [r, t(REGION_KEY[r] as never)])}
                value={form.region}
                onChange={(v) => set("region", v as Region)}
              />
              <Choices
                label={t("diet.choices.activity")}
                options={ACTIVITY_LEVELS.map((a) => [a, t(ACTIVITY_KEY[a] as never)])}
                value={form.activity}
                onChange={(v) => set("activity", v as ActivityLevel)}
              />
              <Field label={t("diet.field.sleep")}>
                <Input
                  inputMode="decimal"
                  value={form.sleepHours}
                  onChange={(e) => set("sleepHours", e.target.value)}
                />
              </Field>
            </Section>

            <div className="flex gap-3">
              <Button variant="outline" className="rounded-full" onClick={() => setStep(0)}>
                {t("diet.back")}
              </Button>
              <Button
                className="flex-1 rounded-full"
                disabled={!step2Valid}
                onClick={() => setStep(2)}
              >
                {t("diet.continue")}
              </Button>
            </div>
          </>
        ) : null}

        {step === 2 ? (
          <>
            <Section title={t("diet.section.medical")}>
              <p className="text-sm text-muted-foreground">
                {t("diet.medical.intro")}
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                {CONDITIONS.map((c) => {
                  const checked =
                    c.key === "none" ? form.conditions.length === 0 : form.conditions.includes(c.key);
                  return (
                    <label
                      key={c.key}
                      className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm transition ${
                        checked ? "border-brand bg-brand-soft/40" : "border-border"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[hsl(var(--brand))]"
                        checked={checked}
                        onChange={() => {
                          if (c.key === "none") return set("conditions", []);
                          set(
                            "conditions",
                            form.conditions.includes(c.key)
                              ? form.conditions.filter((k) => k !== c.key)
                              : [...form.conditions, c.key],
                          );
                        }}
                      />
                      {t(CONDITION_LABEL_KEY[c.key] as never)}
                    </label>
                  );
                })}
              </div>
              <Field label={t("diet.field.medications")}>
                <Input
                  value={form.medications}
                  onChange={(e) => set("medications", e.target.value)}
                  placeholder={t("diet.field.medicationsPlaceholder")}
                />
              </Field>
              <Field label={t("diet.field.allergies")}>
                <Input
                  value={form.allergies}
                  onChange={(e) => set("allergies", e.target.value)}
                  placeholder={t("diet.field.allergiesPlaceholder")}
                />
              </Field>
              <Field label={t(TRACK_QUESTION_KEY[form.track] as never)}>
                <Textarea
                  rows={3}
                  value={form.trackNote}
                  onChange={(e) => set("trackNote", e.target.value)}
                />
              </Field>
              <ConsentBlock
                value={form.consentChannels}
                onChange={(next) => set("consentChannels", next)}
                channels={["email", "whatsapp"]}
              />
              <p className="text-xs leading-relaxed text-muted-foreground">
                Your plan is general nutrition guidance, not medical advice. By continuing you agree
                to our{" "}
                <Link to="/policies/fair-use-policy" className="underline">
                  Fair Use Policy
                </Link>{" "}
                and{" "}
                <Link to="/policies/privacy-policy" className="underline">
                  Privacy Policy
                </Link>
                . Offers are optional — you can opt out any time.
              </p>
            </Section>

            {error ? (
              <p className="rounded-xl bg-destructive/10 p-3 text-sm text-destructive">{error}</p>
            ) : null}

            <div className="flex gap-3">
              <Button variant="outline" className="rounded-full" onClick={() => setStep(1)}>
                {t("diet.back")}
              </Button>
              <Button className="flex-1 rounded-full" disabled={busy} onClick={submit}>
                {busy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("diet.cta.generating")}
                  </>
                ) : (
                  t("diet.cta.submit")
                )}
              </Button>
            </div>
          </>
        ) : null}
      </div>

      <p className="mt-6 flex gap-2 text-xs leading-relaxed text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
        {t("diet.disclaimer")}
      </p>
      <p className="mt-4 text-xs text-muted-foreground">
        {t("diet.quizPrompt")}{" "}
        <Link to="/quiz" className="underline">
          {t("diet.quizLink")}
        </Link>
        .
      </p>

      <section className="mt-12 border-t border-border pt-8">
        <h2 className="font-display text-xl font-semibold">Free diet plan — common questions</h2>
        <dl className="mt-5 space-y-5">
          {DIET_FAQ.map((f) => (
            <div key={f.q}>
              <dt className="text-sm font-semibold">{f.q}</dt>
              <dd className="mt-1 text-sm leading-relaxed text-muted-foreground">{f.a}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-6 text-sm text-muted-foreground">
          Reading up first?{" "}
          <Link to="/blog/pcos-explained-symptoms-diagnosis-india" className="underline">
            PCOS explained
          </Link>{" "}
          and{" "}
          <Link to="/blog/fatty-liver-diet-plan-india" className="underline">
            the fatty liver diet guide
          </Link>{" "}
          pair well with your plan.
        </p>
      </section>
    </main>

  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <h2 className="font-display text-lg font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Label className="block space-y-1.5 text-sm font-normal">
      <span className="block font-medium">{label}</span>
      {children}
    </Label>
  );
}

function Choices({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: (readonly [string, string])[] | string[][];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label className="text-sm">{label}</Label>
      <div className="flex flex-wrap gap-2">
        {(options as string[][]).map(([key, text]) => (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className={`rounded-full border px-4 py-2 text-sm transition ${
              value === key
                ? "border-brand bg-brand-soft/60 text-brand-deep"
                : "border-border hover:border-brand/40"
            }`}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}
